import { useEffect, useState } from "react";
import type { PropsWithChildren } from "react";
import { Navigate } from "react-router-dom";

import { getEffectiveVesselAccess } from "../../api/auth";
import { useAuth } from "../../hooks/useAuth";
import { useAttentionRegisterAuthority } from "../../hooks/useAttentionRegisterAuthority";
import { hasPermission } from "../../lib/permissions";
import type { AuthUser, EffectiveVesselAccess } from "../../types/auth";
import { AttentionRegisterAccessContext } from "./AttentionRegisterAccessContext";

interface PermissionOptions {
  roles?: string[];
  forms?: string[];
  processes?: string[];
}

interface AttentionRegisterPermissionProps extends PropsWithChildren, PermissionOptions {
  vesselId: string;
  anyOf?: PermissionOptions[];
}

type AccessObservation = { key: string; access: EffectiveVesselAccess | null };
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function canonicalRequestedUuid(value: string): string | null {
  const unwrapped = value.replace(/^urn:uuid:/i, "").replace(/^\{(.*)\}$/, "$1");
  const hex = unwrapped.replace(/-/g, "");
  if (!/^[0-9a-f]{32}$/i.test(hex)) return null;
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`.toLowerCase();
}

function stringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((item) => typeof item === "string");
}

export function AttentionRegisterPermission({ vesselId, roles, forms, processes, anyOf, children }: AttentionRegisterPermissionProps) {
  const { user, isAuthenticated, isInitialized, isLoading } = useAuth();
  const authority = useAttentionRegisterAuthority();
  const key = JSON.stringify([authority, vesselId]);
  const validSelection = Boolean(vesselId.trim());
  const [observation, setObservation] = useState<AccessObservation | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!isAuthenticated || !isInitialized || isLoading || !validSelection) {
      return () => { cancelled = true; };
    }
    // Bypass the ordinary stored-scope cache. Bind adoption to this exact authority.
    getEffectiveVesselAccess(vesselId)
      .then((access) => {
        if (cancelled) return;
        const returnedVessel = String(access.vessel_id ?? "").trim();
        // Codes/numeric aliases are canonicalized by the called, authorized owning API.
        const requestedUuid = canonicalRequestedUuid(vesselId);
        const matches = requestedUuid === null || returnedVessel.toLowerCase() === requestedUuid;
        const valid = UUID_PATTERN.test(returnedVessel) && matches
          && typeof access.vessel_name === "string" && typeof access.vessel_code === "string"
          && stringArray(access.form_ids) && stringArray(access.process_ids)
          && stringArray(access.effective_role_codes);
        setObservation({ key, access: valid ? access : null });
      })
      .catch(() => {
        if (!cancelled) setObservation({ key, access: null });
      });
    return () => { cancelled = true; };
  }, [isAuthenticated, isInitialized, isLoading, key, validSelection, vesselId]);

  if (!isInitialized || isLoading) return <div>Loading...</div>;
  if (!isAuthenticated || !user) return <Navigate replace to="/login" />;
  if (!validSelection) return <Navigate replace to="/dashboard" />;
  if (observation?.key !== key) return <div>Loading...</div>;
  const access = observation.access;
  if (!access) return <Navigate replace to="/dashboard" />;
  const permissionUser: AuthUser = {
    ...user, vessel_id: access.vessel_id, vessel_name: access.vessel_name,
    vessel_code: access.vessel_code,
    roles: Array.from(new Set([user.role, ...access.effective_role_codes].filter(Boolean))),
    form_ids: access.form_ids, process_ids: access.process_ids,
  };
  const allowed = hasPermission(permissionUser, { roles, forms, processes })
    || Boolean(anyOf?.some((option) => hasPermission(permissionUser, option)));
  if (!allowed) return <Navigate replace to="/dashboard" />;
  return <AttentionRegisterAccessContext.Provider value={{ user: permissionUser, effectiveAccess: access }}>
    {children}
  </AttentionRegisterAccessContext.Provider>;
}
